import notificationsSettingsService from '@/modules/notifications/services/notificationsSettingsService';
import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';

// Helper function to get setting choice with default value
const getSettingChoice = (setting, key, defaultState = true) => (setting ? setting[key] : defaultState);

// Helper function to get title by locale with fallback
const getTitleByLocale = (titleArray, locale) => titleArray?.find((item) => item.locale === locale)?.value || '';

// Helper function to get description by locale with fallback
const getDescriptionByLocale = (descriptionArray, locale) => descriptionArray?.find((item) => item.locale === locale)?.value || '';

// Group templates by category
const groupTemplatesByCategory = (templatesArray) => {
  const groupedRawTemplates = {};

  templatesArray.forEach((template) => {
    const category = template.category || 'system';
    if (!groupedRawTemplates[category]) {
      groupedRawTemplates[category] = [];
    }
    groupedRawTemplates[category].push(template);
  });

  return groupedRawTemplates;
};

// Map a single template with all required transformations
const mapSingleTemplate = (template, staff, businessLocale, staffSettings) => {
  const displayName = getTitleByLocale(template.title, businessLocale)
    || getTitleByLocale(template.title, 'en') || '';

  const description = getDescriptionByLocale(template.description, businessLocale)
    || getDescriptionByLocale(template.description, 'en') || '';

  // Find matching staff setting for this template
  const matchingSetting = staffSettings.find((setting) => setting.notification_metadata_uid === template.uid);

  return {
    uid: matchingSetting?.uid,
    staff_uid: staff?.uid,
    notification_metadata_uid: template.uid,
    notification_code_name: template.code_name || template.name,
    display_name: displayName,
    description,
    push: getSettingChoice(matchingSetting, 'push'),
    pane: getSettingChoice(matchingSetting, 'pane'),
    email: getSettingChoice(matchingSetting, 'email'),
    sms: getSettingChoice(matchingSetting, 'sms', false),
    display_push: template.delivery_channel?.includes('push') || false,
    display_pane: template.delivery_channel?.includes('pane') || false,
    display_email: template.delivery_channel?.includes('email') || false,
    display_sms: template.delivery_channel?.includes('sms') || false,
    category: template.trigger_type,
  };
};

// Set category flags based on existing templates
const setCategoryFlags = (templatesArray, groupedTemplates) => {
  const result = groupedTemplates;
  templatesArray.forEach((template) => {
    const templateCategory = template.trigger_type;
    if (templateCategory === 'client') {
      result.category.client = true;
    }
    if (templateCategory === 'system') {
      result.category.system = true;
    }
  });
};

// Main function to map and group notification templates
const mapAndGroupNotificationTemplates = (templatesArray, staff, businessLocale, staffSettings = []) => {
  // Filter out notifications that shouldn't be shown in settings
  const filteredTemplates = templatesArray.filter((template) => template.configurable_by_staff);

  // Group templates by category first
  const groupedRawTemplates = groupTemplatesByCategory(filteredTemplates);

  const groupedTemplates = {
    category: {},
  };

  // Map templates for each category
  Object.keys(groupedRawTemplates).forEach((category) => {
    groupedTemplates[category] = groupedRawTemplates[category]
      .map((template) => mapSingleTemplate(template, staff, businessLocale, staffSettings));
  });

  // Set category flags based on templates that exist
  setCategoryFlags(filteredTemplates, groupedTemplates);

  return groupedTemplates;
};

const state = {
  isSuccessfullySaved: false,
  usedApps: [],
  notificationTemplates: {},
  notificationTemplatesForDiscard: {},
};

const getters = {
  isSuccessfullySaved: (storeState) => storeState.isSuccessfullySaved,
  allStaffSettingsArray: (storeState) => {
    const settingsCategoriesKeys = Object.keys(storeState.notificationTemplates);
    if (settingsCategoriesKeys.length > 0) {
      const categoryValues = settingsCategoriesKeys.map((category) => storeState.notificationTemplates[category]);
      const arrayCategories = categoryValues.filter(Array.isArray);
      const flattenedSettings = arrayCategories.reduce((previousValue, currentValue) => previousValue.concat(currentValue), []);
      return flattenedSettings;
    }
    return [];
  },
  usedApps: (storeState) => storeState.usedApps,
  notificationTemplates: (storeState) => storeState.notificationTemplates,
  changedSettingsAmount: (storeState, storeGetters) => {
    if (storeGetters.allStaffSettingsArray) {
      const changedSettings = storeGetters.allStaffSettingsArray
        .filter((setting) => (
          setting.changed?.push || setting.changed?.pane
          || setting.changed?.email || setting.changed?.sms
        ));
      return changedSettings.length;
    }
    return 0;
  },
};

/* eslint-disable no-param-reassign */
const mutations = {
  setIsSuccessfullySaved(storeState, value) {
    storeState.isSuccessfullySaved = value;
  },
  setChangedSetting(storeState, changedItem) {
    Object.keys(storeState.notificationTemplates).every((category) => {
      if (!Array.isArray(storeState.notificationTemplates[category])) return true;
      const changedItemIndex = storeState.notificationTemplates[category]
        .findIndex((currentSetting) => (
          currentSetting.notification_metadata_uid === changedItem.notification_metadata_uid
        ));
      if (changedItemIndex > -1) {
        Vue.set(storeState.notificationTemplates[category], changedItemIndex, {
          ...storeState.notificationTemplates[category][changedItemIndex],
          ...changedItem,
        });
        return false;
      }
      return true;
    });
  },
  setUsedApps(storeState, usedApps) {
    storeState.usedApps = usedApps;
  },
  setNotificationTemplates(storeState, notificationTemplates) {
    storeState.notificationTemplates = notificationTemplates;
    storeState.notificationTemplatesForDiscard = cloneDeep(notificationTemplates);
  },
  resetStaffSettings(storeState) {
    storeState.notificationTemplates = {};
  },
  discardChanges(storeState) {
    storeState.notificationTemplates = cloneDeep(storeState.notificationTemplatesForDiscard);
  },
};
/* eslint-enable no-param-reassign */

const actions = {
  async fetchUsedApps({ commit, rootGetters, state: storeState, dispatch }) {
    if (storeState.usedApps.length === 0) {
      try {
        await dispatch('AppsStore/getAvailableMarketApps', {}, { root: true });
        const apps = rootGetters['AppsStore/availableMarketApps'] || [];
        commit('setUsedApps', apps);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching used apps:', error);
        commit('setUsedApps', []);
      }
    }
  },
  async fetchNotificationTemplates({ commit, rootGetters }) {
    try {
      const rawNotificationTemplates = await notificationsSettingsService.getAllNotificationTemplates();

      // Extract the notification_templates array from the response
      const templatesArray = rawNotificationTemplates.notification_templates || [];

      // Get staff and locale information
      const staffUid = rootGetters['AuthStore/staffUid'];
      const staff = rootGetters['StaffStore/staffData'] || { uid: staffUid };
      const businessLocale = rootGetters['BusinessStore/locale'] || 'en';

      // Fetch staff settings for notification preferences
      const staffSettings = await notificationsSettingsService.getStaffSettings(staffUid);

      // Map and group templates using helper function
      const groupedTemplates = mapAndGroupNotificationTemplates(templatesArray, staff, businessLocale, staffSettings);

      commit('setNotificationTemplates', groupedTemplates);
      return groupedTemplates;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching notification templates:', error);
      commit('setNotificationTemplates', {});
      return error;
    }
  },
  async updateChangedSetting({ commit }, payload) {
    commit('setChangedSetting', payload);
  },
  async saveSettingsChanges({ commit, getters: storeGetters, dispatch }) {
    try {
      const isSomethingChange = (changed) => changed?.push || changed?.pane || changed?.email || changed?.sms;
      const settingsToCreate = storeGetters.allStaffSettingsArray.filter((setting) => !setting.uid && isSomethingChange(setting.changed));
      const settingsToUpdate = storeGetters.allStaffSettingsArray.filter((setting) => setting.uid && isSomethingChange(setting.changed));

      // create
      const createMap = settingsToCreate.map((currentSetting) => notificationsSettingsService.createSingleSetting({ ...currentSetting }));
      // update
      const updateMap = settingsToUpdate.map((currentSetting) => notificationsSettingsService.updateSingleSetting({ ...currentSetting }));

      await Promise.all(createMap.concat(updateMap)).then((returnedValues) => {
        const isSuccessfullySaved = returnedValues.filter((value) => value === undefined).length === 0;
        commit('setIsSuccessfullySaved', isSuccessfullySaved);
      });
      await dispatch('fetchNotificationTemplates');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  },
  async resetStaffSettings({ commit }) {
    commit('resetStaffSettings');
  },
  async discardChanges({ commit }) {
    commit('discardChanges');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
